/**
 * The agent transport, which is this app's to write.
 *
 * The stream goes through the backend's `/agent` proxy, never to the agent service directly: the
 * proxy authenticates the JWT like any other route and mints the X-BONO identity server-side, so
 * this file no longer decides who the caller is. `EventSource` cannot send an Authorization header,
 * so the stream is read with `fetch`.
 *
 * Known limit, accepted: this `fetch` bypasses axios's 401→refresh single-flight, so an expired
 * token surfaces as a readable error and studio degrades down its ladder instead of retrying.
 * The expiry check below turns that from a proxy 401 into a message that names the fix.
 */
import parseJwt from '@/utils/parseJwt'
import { parseSseFrames } from '@bono-packages/studio'
import type { SsePort } from '@bono-packages/studio'

const BASE_URL = import.meta.env.VITE_APP_BASE_URL as string | undefined
const PROXY_PREFIX = import.meta.env.VITE_APP_AGENT_PROXY_PREFIX as string | undefined

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token')
  const idToken = localStorage.getItem('idToken')
  const companyId =
    localStorage.getItem('companyId')?.trim() ||
    (idToken ? parseJwt(idToken)?.sub : '') ||
    ''

  const headers: Record<string, string> = { authorization: `Bearer ${token ?? ''}` }
  if (idToken) headers['X-ID-TOKEN'] = idToken
  if (companyId) headers['X-COMPANY-ID'] = companyId
  return headers
}

/** `null` when no agent proxy is configured — studio reads that as "this app has no agent". */
export const agentPort: SsePort | null = (!BASE_URL || !PROXY_PREFIX)
  ? null
  : async (path, onEvent, signal) => {
      // The token is read fresh per call, but a stale one can still be sitting in localStorage
      // right after axios refreshed in another tab or mid-flight. Saying so beats sending it and
      // letting the proxy's 401 arrive as a bare "Agent responded 401".
      const token = localStorage.getItem('token')
      const exp = token ? parseJwt(token)?.exp : null
      if (typeof exp === 'number' && exp * 1000 < Date.now()) {
        throw new Error('Your session token has expired — reload the page to sign in again.')
      }

      const url =
        BASE_URL.replace(/\/+$/, '') + '/' + PROXY_PREFIX.replace(/^\/+|\/+$/g, '') + path

      const response = await fetch(url, { signal, headers: authHeaders() })

      if (!response.ok || !response.body) {
        const detail = await response.text().catch(() => '')
        throw new Error(detail.slice(0, 200) || `Agent responded ${response.status}`)
      }

      // The frame shape belongs to studio: it was measured against this service.
      await parseSseFrames(response.body, onEvent)
    }
